import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import {
  CommodityType,
  CreateOrUpdateOfficeChargeRequest,
  Currency,
  IOfficeAirportCharge,
  IOfficeCharge,
  OfficeChargeCategory,
  OfficeChargeUnitOfMeasure,
  OfficeService,
  PermissionSetName,
} from '@cai-services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PaginatePipeArgs } from 'ngx-pagination';
import { APP_PROPS } from '../../../cai-common.module';
import {
  AirportLight,
  ApplicationProperties,
  GlobalService,
  Office,
  User,
} from '../../../core';
import { FormUtil, OfficeUtil, SearchUtil } from '../../../utils';
import {
  greaterThanControlValidator,
  isNumberValidator,
  maxDecimalPlacesValidator,
  requiredIfCheckboxCheckedValidator,
} from '../../../validators';
import { OfficeAddAirportChargeModal } from '../modals/office-add-airport-charge-modal/office-add-airport-charge-modal.component';
import { OfficeDeleteChargeModal } from '../modals/office-delete-charge-modal/office-delete-charge-modal.component';

@Component({
  selector: 'cai-office-charges',
  templateUrl: './office-charges.component.html',
  styleUrls: ['./office-charges.component.scss'],
  standalone: false,
})
export class OfficeChargesComponent implements OnInit, OnChanges {
  @Input() activeOffice: Office;
  @Input() currentUser: User;
  activeOfficeCharges: IOfficeCharge[] = [];
  @ViewChild('officeNameInput') officeNameInput: ElementRef;
  iAmAdmin = false;

  private readonly defaultServiceNames: string[] = [
    'Handling charges',
    'Security charges',
    'MAWB charges',
    'FHL/FWB charges',
    'Export customs charges',
    'Pick-up charges',
  ];

  getOfficeName = OfficeUtil.getOfficeName;

  officeMarginForm: FormGroup;
  editServicesForm: FormGroup[];

  PREVIOUS_LABEL = $localize`:@@global.previous:Previous`;
  NEXT_LABEL = $localize`:@@global.next:Next`;

  isLoading = true;
  isEditingOfficeMargin = false;
  isEditingService: boolean[] = [];
  canDeleteService: boolean[] = [];

  pagination: PaginatePipeArgs = {
    itemsPerPage: 10,
    currentPage: 1,
    totalItems: 0,
  };

  sortState: Sort = {
    active: 'id.name',
    direction: 'asc',
  };

  search: string = null;
  allCurrencies: Currency[] = [];
  allAirports: AirportLight[] = [];
  officeServiceTooltipError = '';
  OfficeChargeUnitOfMeasure = OfficeChargeUnitOfMeasure;
  OfficeChargeCategory = OfficeChargeCategory;
  commodityType = CommodityType;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  hasDraftCharge = false;
  officeAirportCharges: IOfficeAirportCharge[];
  selectedAirportCharges: IOfficeAirportCharge | undefined;
  airportSelectorOptions: any[];

  chargeFilter: {
    type?: string;
    uom?: string;
    default?: boolean;
  } = {};

  constructor(
    @Inject(APP_PROPS)
    private readonly appProperties: ApplicationProperties,
    private readonly officeService: OfficeService,
    private readonly globalService: GlobalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: NgbModal,
  ) {}

  async ngOnInit(): Promise<void> {
    this.allAirports = await this.globalService.getAirports();
    this.allCurrencies = await this.globalService.getAllCurrencies();
    this.initializeOfficeMarginForm();
    await this.fetchOfficeAirportCharges();
  }

  async fetchOfficeAirportCharges(): Promise<void> {
    try {
      this.officeAirportCharges =
        await this.officeService.getOfficeAirportCharges(this.activeOffice.id);
      this.airportSelectorOptions = SearchUtil.fillAirportAutocompleteOptions(
        this.allAirports.filter((airport) =>
          this.officeAirportCharges.some(
            (airportCharge) =>
              airportCharge.airport.airportCode === airport.airportCode,
          ),
        ),
      );
    } catch (e) {
      console.error(e);
    }

    await this.onSelectAirport(
      this.officeAirportCharges?.[0]?.airport?.airportCode,
    );

    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('activeOffice')) {
      this.onActiveOfficeChange();
    }
  }

  async onActiveOfficeChange() {
    this.iAmAdmin = !!this.currentUser.officeAccesses?.find(
      (oa) =>
        oa.office.id === this.activeOffice.id &&
        oa.roles.find((p) => p.name === PermissionSetName.OFFICE_ADMIN),
    );
    this.initializeOfficeMarginForm();
    this.stopEditingOfficeMargin();
    await this.fetchOfficeAirportCharges();
    this.onPageChanged(1);
  }

  private populateDefaultServicesIfNecessary() {
    const missingDefaultServiceNames = this.defaultServiceNames.filter(
      (serviceName) =>
        !this.activeOfficeCharges
          .map((service) => service.name)
          .includes(serviceName),
    );

    for (const serviceName of missingDefaultServiceNames) {
      // we don't want to push any items to the next page
      if (+this.pagination.totalItems >= 10) {
        break;
      }
      this.activeOfficeCharges.push({
        name: serviceName,
        chargeCategory: 'EXPORT',
      } as any);
      this.pagination.totalItems = +this.pagination.totalItems + 1;
    }
  }

  private initializeServicesTableConfig(): void {
    this.isEditingService = this.activeOfficeCharges.map((_) => false);
    this.canDeleteService = this.activeOfficeCharges.map(
      (service) => service.markupFlatRate !== undefined || service.markupPercent !== undefined,
    );
    this.hasDraftCharge = false;
  }

  createDraftOfficeService(): void {
    const draftOfficeService: any = {};
    draftOfficeService.chargeCategory = OfficeChargeCategory.ALL;
    const draftFormGroup = this.initializeServiceFormGroup(draftOfficeService);
    this.hasDraftCharge = true;
    this.activeOfficeCharges.unshift(draftOfficeService);
    this.editServicesForm.unshift(draftFormGroup);
    this.isEditingService.unshift(true);
    this.canDeleteService.unshift(false);
    this.cdr.detectChanges();
  }

  sortData(sort: Sort) {
    this.sortState = sort;
    this.fetchOfficeAirportCharges();
  }

  private initializeOfficeMarginForm() {
    this.officeMarginForm = new FormGroup({
      margin: new FormControl(
        {
          value: this.activeOffice.marginPercent
            ? this.activeOffice.marginPercent * 100
            : this.activeOffice.marginFlat ?? '',
          disabled: true,
        },
        [
          Validators.required,
          isNumberValidator(),
          Validators.min(0.01),
          maxDecimalPlacesValidator(2),
        ],
      ),
      isMarginPercent: new FormControl(
        {
          value: !(this.activeOffice.marginFlat > 0),
          disabled: true,
        },
      ),
      marginCurrencyCode: new FormControl(
        {
          value: this.activeOffice.marginCurrency?.currencyCode ?? '',
          disabled: true,
        },
        [Validators.required],
      ),
      marginMinFlat: new FormControl(
        { value: this.activeOffice.marginMinFlat ?? '', disabled: true },
        [
          isNumberValidator(),
          Validators.min(0),
          Validators.max(1000000),
          maxDecimalPlacesValidator(2),
        ],
      ),
      marginMaxFlat: new FormControl(
        { value: this.activeOffice.marginMaxFlat ?? '', disabled: true },
        [
          isNumberValidator(),
          Validators.min(0),
          Validators.max(1000000),
          maxDecimalPlacesValidator(2),
          greaterThanControlValidator('marginMinFlat'),
        ],
      ),
    });

    this.officeMarginForm.get('marginMinFlat').valueChanges.subscribe((_) => {
      this.officeMarginForm.get('marginMaxFlat').updateValueAndValidity();
    });
  }

  private initializeEditServicesForm() {
    this.editServicesForm = this.activeOfficeCharges.map((service) =>
      this.initializeServiceFormGroup(service),
    );
  }

  private initializeServiceFormGroup(service: IOfficeCharge): FormGroup {
    const formGroup = new FormGroup({
      name: new FormControl(service.name ?? '', [Validators.required]),
      markup: new FormControl(service.markupPercent > 0 ? service.markupPercent * 100 : service.markupFlatRate, [
        isNumberValidator(),
        Validators.min(0),
      ]),
      isMarkupFlat: new FormControl(!(service.markupPercent > 0)),
      buyingRate: new FormControl(service.buyingRate ?? '', [
        isNumberValidator(),
        Validators.min(0)
      ]),
      sellingRate: new FormControl({
        value: service.sellingRate ?? '',
        disabled: service.buyingRate && service.buyingRate >= 0
      }, [
        Validators.required,
        isNumberValidator(),
        Validators.min(0)
      ]),
      markupMinFlat: new FormControl(service.markupMinFlat ?? '', [
        isNumberValidator(),
        Validators.min(0),
      ]),
      markupMaxFlat: new FormControl(service.markupMaxFlat ?? '', [
        isNumberValidator(),
        Validators.min(0),
        greaterThanControlValidator('markupMinFlat'),
      ]),
      unitOfMeasure: new FormControl(service.unitOfMeasure, [
        Validators.required,
      ]),
      chargeCategory: new FormControl(service.chargeCategory, []),
      defaultCommodity: new FormControl(
        {
          value: service.defaultCommodity,
          disabled: !service.isDefault,
        },
        [requiredIfCheckboxCheckedValidator('isDefault')],
      ),
      isDefault: new FormControl(service.isDefault ?? false, [
        Validators.required,
      ]),
    });

    formGroup.get('markupMinFlat').valueChanges.subscribe((_) => {
      formGroup.get('markupMaxFlat').updateValueAndValidity();
    });

    formGroup.get('isDefault').valueChanges.subscribe((isDefault) => {
      const defaultCommodityControl = formGroup.get('defaultCommodity');
      if (isDefault) {
        defaultCommodityControl.enable();
      } else {
        defaultCommodityControl.disable();
      }
    });

    formGroup.get('buyingRate').valueChanges.subscribe(_ => this.recalculateSellingRate(formGroup));
    formGroup.get('markup').valueChanges.subscribe(_ => this.recalculateSellingRate(formGroup));
    formGroup.get('isMarkupFlat').valueChanges.subscribe(_ => this.recalculateSellingRate(formGroup));
    return formGroup;
  }

  private recalculateSellingRate(formGroup: FormGroup): void {
    const buyingRate = +formGroup.get('buyingRate').value;
    const markup = +formGroup.get('markup').value;
    const sellingRateControl = formGroup.get('sellingRate');
    if (!(buyingRate > 0) || !(markup >= 0)) {
      sellingRateControl.enable();
      return;
    }

    const sellingRate = formGroup.get('isMarkupFlat').value === true
      ? buyingRate + markup
      : buyingRate + (buyingRate * (markup / 100));
    sellingRateControl.setValue(sellingRate);
    sellingRateControl.disable();
  }

  startEditingOfficeMargin() {
    this.isEditingOfficeMargin = true;
    this.officeMarginForm.enable();
  }

  async saveOfficeMarginChanges(): Promise<void> {
    try {
      const isMarginPercent = this.officeMarginForm.get('isMarginPercent').value === true;

      const absoluteMarginPercent = isMarginPercent
        ? +this.officeMarginForm.get('margin').value / 100
        : 0;
      await this.officeService.patchOfficeMargin(this.activeOffice.id, {
        marginPercent: absoluteMarginPercent,
        marginFlat: isMarginPercent
          ? 0
          : this.officeMarginForm.get('margin').value,
        marginCurrencyCode:
          this.officeMarginForm.get('marginCurrencyCode').value,
        marginMinFlat: this.officeMarginForm.get('marginMinFlat').value,
        marginMaxFlat: this.officeMarginForm.get('marginMaxFlat').value,
      });

      this.activeOffice.marginPercent = absoluteMarginPercent;
      this.activeOffice.marginCurrency = this.allCurrencies.find(
        (currency) =>
          currency.currencyCode ===
          this.officeMarginForm.get('marginCurrencyCode').value,
      );
      this.activeOffice.marginMinFlat =
        this.officeMarginForm.get('marginMinFlat').value;
      this.activeOffice.marginMaxFlat =
        this.officeMarginForm.get('marginMaxFlat').value;
      this.stopEditingOfficeMargin();
      this.cdr.detectChanges();
    } catch (e) {
      console.error(e);
    }
  }

  private stopEditingOfficeMargin() {
    this.isEditingOfficeMargin = false;
    this.officeMarginForm.disable();
  }

  onDiscardOfficeMarginChanges() {
    this.initializeOfficeMarginForm();
    this.stopEditingOfficeMargin();
  }

  trackById(index: number, service: IOfficeCharge) {
    return service.name;
  }

  onPageChanged(page: number) {
    this.pagination.currentPage = page;
    this.fetchOfficeAirportCharges();
  }

  onSearch(query: string) {
    this.search = query;
    this.fetchOfficeAirportCharges();
  }

  get isCargoWallet(): boolean {
    return this.appProperties.name === 'cargowallet';
  }

  isOfficeMarginControlHasError(
    controlName: string,
    validationType: string,
  ): boolean {
    const control = this.officeMarginForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType);
  }

  onHoverFormField(formGroup: FormGroup, controlName: string): void {
    this.officeServiceTooltipError = FormUtil.getFirstError(
      formGroup,
      controlName,
    );
  }

  startEditingOfficeService(index: number): void {
    this.isEditingService[index] = true;
  }

  async saveOfficeServiceChanges(index: number): Promise<void> {
    try {
      const formGroup = this.editServicesForm[index],
        updatedName = formGroup.get('name').value,
        oldName = this.activeOfficeCharges[index].name ?? updatedName,
        markupValue = formGroup.get('markup').value ?? 0,
        isMarkupFlat = formGroup.get('isMarkupFlat').value,
        isDefault = formGroup.get('isDefault').value,
        buyingRateValue = formGroup.get('buyingRate').value,
        markupMinFlatValue = formGroup.get('markupMinFlat').value,
        markupMaxFlatValue = formGroup.get('markupMaxFlat').value,
        updatedCharge: CreateOrUpdateOfficeChargeRequest = {
          name: updatedName,
          markupFlatRate: isMarkupFlat ? markupValue : 0,
          markupPercent: isMarkupFlat ? 0 : markupValue / 100,
          buyingRate: buyingRateValue ? +buyingRateValue : undefined,
          sellingRate: +formGroup.get('sellingRate').value,
          markupMinFlat: markupMinFlatValue ? +markupMinFlatValue : undefined,
          markupMaxFlat: markupMaxFlatValue ? +markupMaxFlatValue : undefined,
          unitOfMeasure: formGroup.get('unitOfMeasure').value,
          isDefault,
          defaultCommodity: isDefault
            ? formGroup.get('defaultCommodity').value
            : undefined,
          chargeCategory: formGroup.get?.('chargeCategory')?.value || undefined,
        };
      await this.officeService.createOrUpdateOfficeService(
        this.activeOffice.id,
        this.selectedAirportCharges.airport.airportCode,
        oldName,
        updatedCharge,
      );

      if (this.isDraftCharge(this.activeOfficeCharges[index])) {
        this.hasDraftCharge = false;
      }
      this.activeOfficeCharges[index].name = updatedName;
      this.activeOfficeCharges[index].markupFlatRate =
        updatedCharge.markupFlatRate;
      this.activeOfficeCharges[index].markupPercent =
        updatedCharge.markupPercent;
      this.activeOfficeCharges[index].buyingRate =
        updatedCharge.buyingRate;
      this.activeOfficeCharges[index].sellingRate =
        updatedCharge.sellingRate;
      this.activeOfficeCharges[index].markupMinFlat =
        updatedCharge.markupMinFlat;
      this.activeOfficeCharges[index].markupMaxFlat =
        updatedCharge.markupMaxFlat;
      this.activeOfficeCharges[index].unitOfMeasure =
        updatedCharge.unitOfMeasure;
      this.activeOfficeCharges[index].isDefault = updatedCharge.isDefault;
      this.activeOfficeCharges[index].defaultCommodity =
        updatedCharge.defaultCommodity;
      this.activeOfficeCharges[index].chargeCategory =
        updatedCharge.chargeCategory;
      this.isEditingService[index] = false;
      this.canDeleteService[index] = true;
    } catch (e) {
      console.error(e);
    }
    this.cdr.detectChanges();
  }

  async onDeleteServiceButtonClick(index: number): Promise<void> {
    const deleteServiceModal = this.modalService.open(OfficeDeleteChargeModal, {
        centered: true,
        size: 'md',
      }),
      officeChargeToDelete = this.activeOfficeCharges[index];
    deleteServiceModal.componentInstance.activeOffice = this.activeOffice;
    deleteServiceModal.componentInstance.selectedAirportCharges =
      this.selectedAirportCharges;
    deleteServiceModal.componentInstance.officeChargeToDelete =
      officeChargeToDelete;
    deleteServiceModal.componentInstance.modalRef = deleteServiceModal;

    try {
      await deleteServiceModal.result;
      if (this.defaultServiceNames.includes(officeChargeToDelete.name)) {
        this.activeOfficeCharges[index] = {
          name: officeChargeToDelete.name,
        } as any;
        this.canDeleteService[index] = false;
      } else {
        this.removeTableRowAt(index);
      }
    } catch (e) {
      console.error(e);
    }
    this.cdr.detectChanges();
  }

  discardOfficeServiceChanges(index: number): void {
    this.isEditingService[index] = false;
    this.editServicesForm[index] = this.initializeServiceFormGroup(
      this.activeOfficeCharges[index],
    );

    if (this.isDraftCharge(this.activeOfficeCharges[index])) {
      this.removeTableRowAt(index);
      this.hasDraftCharge = false;
    }

    this.cdr.detectChanges();
  }

  private removeTableRowAt(index: number): void {
    this.activeOfficeCharges.splice(index, 1);
    this.editServicesForm.splice(index, 1);
    this.isEditingService.splice(index, 1);
    this.canDeleteService.splice(index, 1);
  }

  private isDraftCharge(officeService: IOfficeCharge): boolean {
    return officeService.name === undefined;
  }

  async onSelectAirport(airportCode: string | undefined): Promise<void> {
    if (!airportCode) {
      return;
    }
    if (airportCode === undefined) {
      this.selectedAirportCharges = undefined;
      return;
    }

    this.selectedAirportCharges = this.officeAirportCharges.find(
      (airportCharges) => airportCharges.airport.airportCode === airportCode,
    );

    this.isLoading = true;
    const page = this.pagination.currentPage as number,
      sort = `${this.sortState.active},${this.sortState.direction}`;
    try {
      const pagedResults = await this.officeService.getOfficeCharges(
        this.activeOffice.id,
        this.selectedAirportCharges.airport.airportCode,
        page - 1,
        this.pagination.itemsPerPage as number,
        sort,
      );
      this.activeOfficeCharges = pagedResults.content;
      this.pagination.totalItems = pagedResults.totalElements;
    } catch (e) {
      this.activeOfficeCharges = [];
      this.pagination.totalItems = 0;
      console.error(e);
    }
    if (this.iAmAdmin) {
      this.populateDefaultServicesIfNecessary();
    }

    this.initializeEditServicesForm();
    this.initializeServicesTableConfig();
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  async onAddAirportButtonClick(): Promise<void> {
    const addAirportChargeModal = this.modalService.open(
      OfficeAddAirportChargeModal,
      {
        centered: true,
        modalDialogClass: 'add-airport-charge-modal',
      },
    );
    addAirportChargeModal.componentInstance.allCurrencies = this.allCurrencies;
    addAirportChargeModal.componentInstance.activeOffice = this.activeOffice;
    addAirportChargeModal.componentInstance.officeAirportCharges =
      this.officeAirportCharges;
    addAirportChargeModal.componentInstance.modalRef = addAirportChargeModal;

    try {
      await addAirportChargeModal.result;
      await this.fetchOfficeAirportCharges();
    } catch {}
  }

  async onSelectAirportChargeCurrency(currencyCode: string): Promise<void> {
    try {
      await this.officeService.patchOfficeAirportChargeCurrency(
        this.activeOffice.id,
        this.selectedAirportCharges.airport.airportCode,
        currencyCode,
      );
    } catch (e) {
      console.error(e);
    }
  }
}
